import { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from 'antd';

import { IFlat } from '../../common/model/flat.model';
import useI18n from '../../common/hooks/useI18n';

import ShareModal from './shareModal';
import { GalleryButton, ImageCarousel } from '../shared';

type Props = {
  flat: IFlat;
  onShowVirtualVisitButtonClick: () => void;
  onShowAllPhotosButtonClick: () => void;
  className?: string;
};

const ShareIcon = styled.div`
  width: 22px;
  height: 22px;
  background-image: url(/images/share_icon.svg);
  background-size: 22px 22px;
  background-repeat: no-repeat;
  background-position: center center;
  cursor: pointer;
  z-index: 1;
  margin-left: 6px;
  margin-right: 6px;
`;

const VirtualVisitButton = styled(Button)`
  font-weight: 500;
  border-width: 2px;
  margin-right: 8px;
  color: ${(props) => props.theme.colors.primary};
  border-color: ${(props) => props.theme.colors.primary};

  &:active,
  &:hover,
  &:focus {
    color: ${(props) => props.theme.colors.primary};
    border-color: ${(props) => props.theme.colors.primary};
  }
`;

const FlatImageCarousel = ({
  flat,
  onShowVirtualVisitButtonClick,
  onShowAllPhotosButtonClick,
  className,
}: Props): JSX.Element => {
  const i18n = useI18n();

  const [isModalVisible, setIsModalVisible] = useState(false);

  const buttons = useMemo(() => {
    return [
      flat.matterportUrl ? (
        <VirtualVisitButton
          key="virtual-visit-button"
          type="ghost"
          className="semi-ghost"
          onClick={() => onShowVirtualVisitButtonClick()}
        >
          {i18n.t('flatDetail.actions.virtualVisit')}
        </VirtualVisitButton>
      ) : null,
      <GalleryButton
        key="gallery-button"
        className="semi-ghost"
        onClick={onShowAllPhotosButtonClick}
      />,
      <ShareIcon
        key="share-button"
        as="div"
        onClick={() => setIsModalVisible(true)}
      />,
    ].filter((button) => button !== null);
  }, [
    flat.matterportUrl,
    i18n,
    onShowAllPhotosButtonClick,
    onShowVirtualVisitButtonClick,
  ]);

  return (
    <div className={className}>
      <ImageCarousel
        pictureUrls={flat.pictureUrls}
        buttons={buttons}
        fullWidth
      ></ImageCarousel>

      <ShareModal
        isVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        flat={flat}
      />
    </div>
  );
};

export default styled(FlatImageCarousel)`
  position: relative;
  height: 72vh;

  @media ${(props) => props.theme.breakpoints.md} {
    height: 33rem;
  }
  @media ${(props) => props.theme.breakpoints.smd} {
    height: 16.5rem;
    border-radius: unset;
  }
`;
